// MessageForm.js
// Single component for both create and edit, mirroring
// CourseConfiguration.js's pattern: optional :id route param, blank
// defaults when absent, fetches existing data only if id is present.
import React, { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function MessageForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [message, setMessage] = useState({ title: '', body: '' });
  const [status, setStatus] = useState('draft');
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    axios.get(`${API_BASE_URL}/messages/get_message.php?id=${id}`, { withCredentials: true })
      .then(response => {
        if (response.data.error) throw new Error(response.data.error);
        setMessage({ title: response.data.title, body: response.data.body });
        setStatus(response.data.status);
      })
      .catch(error => {
        Swal.fire({ icon: 'error', text: error.message || 'Failed to load message.' });
        navigate('/messages');
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMessage(prev => ({ ...prev, [name]: value }));
  };

  const isSent = isEditing && status === 'sent';

  const handleSaveDraft = () => {
    if (!message.title.trim() || !message.body.trim()) {
      Swal.fire({ icon: 'warning', text: 'Title and body are required.' });
      return;
    }
    setSaving(true);
    axios.post(`${API_BASE_URL}/messages/save_message.php`, {
      id: id || undefined,
      isUpdate: isEditing,
      title: message.title,
      body: message.body,
    }, { withCredentials: true })
      .then(response => {
        if (!response.data.success) throw new Error(response.data.message);
        Swal.fire({ icon: 'success', text: 'Message saved as draft.' });
        navigate('/messages');
      })
      .catch(error => Swal.fire({ icon: 'error', text: error.message || 'Failed to save message.' }))
      .finally(() => setSaving(false));
  };

  const handleSendNow = async () => {
    if (!message.title.trim() || !message.body.trim()) {
      Swal.fire({ icon: 'warning', text: 'Title and body are required.' });
      return;
    }
    const result = await Swal.fire({
      icon: 'warning',
      text: 'Once sent, this message can no longer be edited. Continue?',
      showCancelButton: true,
      confirmButtonText: 'Yes, send it',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    setSaving(true);
    try {
      const saveResponse = await axios.post(`${API_BASE_URL}/messages/save_message.php`, {
        id: id || undefined,
        isUpdate: isEditing,
        title: message.title,
        body: message.body,
      }, { withCredentials: true });
      if (!saveResponse.data.success) throw new Error(saveResponse.data.message);

      const messageId = saveResponse.data.id;
      const sendResponse = await axios.post(`${API_BASE_URL}/messages/send_message.php`, { id: messageId }, { withCredentials: true });
      if (!sendResponse.data.success) throw new Error(sendResponse.data.message);

      Swal.fire({ icon: 'success', text: 'Message sent.' });
      navigate('/messages');
    } catch (error) {
      Swal.fire({ icon: 'error', text: error.message || 'Failed to send message.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  return (
    <div className="p-2">
      <h2>{isEditing ? 'Edit Message' : 'New Message'}</h2>
      {isSent && (
        <p className="text-muted">This message has already been sent and can no longer be edited.</p>
      )}
      <Form>
        <Form.Group controlId="title" className="mb-3">
          <Form.Label>Title</Form.Label>
          <Form.Control
            type="text"
            name="title"
            value={message.title}
            onChange={handleChange}
            readOnly={isSent}
          />
        </Form.Group>
        <Form.Group controlId="body" className="mb-3">
          <Form.Label>Message</Form.Label>
          <Form.Control
            as="textarea"
            rows={6}
            name="body"
            value={message.body}
            onChange={handleChange}
            readOnly={isSent}
          />
        </Form.Group>
        {!isSent && (
          <>
            <Button variant="secondary" className="me-2" onClick={handleSaveDraft} disabled={saving}>
              Save Draft
            </Button>
            <Button variant="primary" onClick={handleSendNow} disabled={saving}>
              Send Now
            </Button>
          </>
        )}
      </Form>
    </div>
  );
}

export default MessageForm;
